import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useHistory } from "react-router-dom";
import supabase from "../lib/supabaseClient";
import Navigation from "../components/navigation";
import Footer from "../components/footer";
import "./dashboard.css";

const Dashboard = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");

  // simple “recent” placeholders you can later replace with real DB queries
  const quickStats = useMemo(() => {
    // Example: if you’re storing generated plans locally for now:
    const savedPlan = localStorage.getItem("sylliplan_plan_text") || "";
    const planCount = savedPlan ? 1 : 0;

    return [
      { label: "Saved plans", value: planCount },
      { label: "Upcoming deadlines", value: 0 },
      { label: "Study sessions", value: 0 },
    ];
  }, []);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        setLoading(true);

        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (!mounted) return;

        const s = data?.session || null;
        setSession(s);

        // Not logged in? send to login
        if (!s) {
          history.replace("/login");
          return;
        }
      } catch (e) {
        if (mounted) setError(e.message || "Failed to load dashboard");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    // Keep session in sync if it changes
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      if (!newSession) history.replace("/login");
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
    };
  }, [history]);

  const onLogout = async () => {
    setError("");
    try {
      await supabase.auth.signOut();
      history.push("/login");
    } catch (e) {
      setError(e.message || "Logout failed");
    }
  };

  const userEmail = session?.user?.email || "Account";

  return (
    <div className="dashboard-page">
      <Helmet>
        <title>Dashboard — Sylliplan</title>
      </Helmet>

      {/* Use your existing site nav so it matches the rest of the site */}
      <Navigation />

      <main className="dash-shell">
        <div className="dash-header">
          <div className="dash-header-left">
            <h1 className="dash-title">Dashboard</h1>
            <p className="dash-subtitle">
              Welcome back, <span className="dash-accent">{userEmail}</span>
            </p>
          </div>

          <div className="dash-header-right">
            <button className="dash-btn ghost" onClick={onLogout}>
              Log out
            </button>
            <Link className="dash-btn primary" to="/syllabus-upload-planner">
              New plan
            </Link>
          </div>
        </div>

        {error ? <div className="dash-alert">{error}</div> : null}

        {loading ? (
          <div className="dash-loading">Loading your dashboard…</div>
        ) : (
          <>
            {/* Quick stats row */}
            <section className="dash-stats">
              {quickStats.map((s) => (
                <div key={s.label} className="dash-stat-card">
                  <div className="dash-stat-value">{s.value}</div>
                  <div className="dash-stat-label">{s.label}</div>
                </div>
              ))}
            </section>

            {/* Feature grid (matches your home cards vibe) */}
            <section className="dash-grid">
              <Link to="/syllabus-upload-planner" className="dash-card">
                <div className="dash-card-icon">⬆️</div>
                <div className="dash-card-title">Syllabus Upload</div>
                <div className="dash-card-text">
                  Upload a PDF/DOCX syllabus and generate your semester plan.
                </div>
                <div className="dash-card-cta">Open planner →</div>
              </Link>

              <Link to="/ai-study-helper" className="dash-card">
                <div className="dash-card-icon">🧠</div>
                <div className="dash-card-title">AI Study Tool</div>
                <div className="dash-card-text">
                  Create study guides and practice quizzes from your course materials.
                </div>
                <div className="dash-card-cta">Open AI helper →</div>
              </Link>

              <div className="dash-card dash-card-disabled">
                <div className="dash-card-icon">📅</div>
                <div className="dash-card-title">Calendar Sync</div>
                <div className="dash-card-text">
                  Sync tasks to Google Calendar / iCal / Outlook (hook this up next).
                </div>
                <div className="dash-card-cta muted">Coming next</div>
              </div>

              <div className="dash-card dash-card-disabled">
                <div className="dash-card-icon">⏰</div>
                <div className="dash-card-title">Deadlines & Reminders</div>
                <div className="dash-card-text">
                  Smart reminders before major assignments are due.
                </div>
                <div className="dash-card-cta muted">Coming next</div>
              </div>

              <div className="dash-card dash-card-disabled">
                <div className="dash-card-icon">📈</div>
                <div className="dash-card-title">Progress Tracking</div>
                <div className="dash-card-text">
                  Track what you’ve completed and what’s next.
                </div>
                <div className="dash-card-cta muted">Coming next</div>
              </div>

              <Link to="/account-subscriptions" className="dash-card">
                <div className="dash-card-icon">✅</div>
                <div className="dash-card-title">Subscriptions</div>
                <div className="dash-card-text">
                  Manage billing, subscription status, and plan access.
                </div>
                <div className="dash-card-cta">Manage billing →</div>
              </Link>
            </section>

            {/* “Recent activity” block (safe placeholder that still looks legit) */}
            <section className="dash-lower">
              <div className="dash-panel">
                <div className="dash-panel-title">Recent activity</div>
                <div className="dash-panel-body">
                  <div className="dash-row">
                    <span className="dash-dot" />
                    <span>Signed in</span>
                    <span className="dash-muted">just now</span>
                  </div>

                  <div className="dash-row">
                    <span className="dash-dot dim" />
                    <span>Generate a plan to see it here</span>
                    <span className="dash-muted">—</span>
                  </div>
                </div>
              </div>

              <div className="dash-panel">
                <div className="dash-panel-title">Quick actions</div>
                <div className="dash-panel-body">
                  <Link className="dash-btn full primary" to="/syllabus-upload-planner">
                    Upload syllabus
                  </Link>
                  <Link className="dash-btn full ghost" to="/ai-study-helper">
                    Create a study guide
                  </Link>
                  <Link className="dash-btn full ghost" to="/account-subscriptions">
                    View subscription
                  </Link>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Dashboard;
